<div class="dd-root" (click)="onRootClick()">

  <!-- ── Left Nav ─────────────────────────────────────────────────────── -->
  <div class="dd-left-nav" (click)="$event.stopPropagation()">

    <div class="dd-nav-header">
      <h3 class="dd-nav-title">Signatories</h3>
      <p class="dd-nav-hint">Click a name to place a signature box</p>
    </div>

    <div class="dd-signer-list">
      <div
        *ngFor="let signer of signers; let i = index"
        class="dd-signer-item"
        [class.dd-signer-active]="carryingSigner === signer"
        [class.dd-signer-readonly]="signer?.isReadOnly"
        (click)="onSignerClick($event, signer)"
      >
        <span class="dd-signer-dot" [style.background]="signerColors[i]"></span>
        <span class="dd-signer-name">{{ signer?.name || signer }}</span>
        <span
          class="dd-signer-count"
          [style.background]="signerColors[i]"
        >{{ getTotalCountForSigner(signer) }}</span>
      </div>
    </div>

    <!-- Pre-defined positions already configured by other signatories.
         Shown for reference only — they cannot be moved or removed here. -->
    <div class="dd-predefined-section" *ngIf="hasReferenceMarkers()">
      <div class="dd-predefined-title">
        <i class="pi pi-lock"></i>
        <span>Pre-defined positions</span>
      </div>
      <p class="dd-predefined-hint">Already placed — shown for reference, not editable here.</p>
      <div class="dd-predefined-list">
        <div
          class="dd-predefined-item"
          *ngFor="let marker of referenceMarkers"
          [style.border-color]="getReferenceColor(marker) + '66'"
        >
          <span
            class="dd-predefined-swatch"
            [style.border-color]="getReferenceColor(marker)"
            [style.background-image]="getReferenceStripes(marker)"
          ></span>
          <span class="dd-predefined-name" [style.color]="getReferenceColor(marker)">
            {{ marker.name }}
          </span>
          <span
            class="dd-predefined-pos"
            [style.color]="getReferenceColor(marker)"
            [style.background]="getReferenceColor(marker) + '14'"
          >{{ marker.positionLabel }}</span>
        </div>
      </div>
    </div>

    <div class="dd-nav-actions">
      <span *ngIf="checkValidity()" class="dd-validity-hint">
        Each signatory needs at least one signature box before submitting.
      </span>
      <div class="dd-btn-row">
        <p-button
          label="Submit"
          icon="pi pi-check"
          [disabled]="checkValidity()"
          (onClick)="onSubmit()"
          styleClass="p-button-sm dd-full-btn"
        ></p-button>
        <p-button
          label="Discard"
          icon="pi pi-times"
          (click)="onCancel()"
          styleClass="p-button-sm p-button-outlined dd-full-btn"
        ></p-button>
      </div>
    </div>
  </div>

  <!-- ── Pages Area ──────────────────────────────────────────────────── -->
  <div class="dd-pages-area">
    <!-- Virtualized: only pages near the viewport (plus a buffer) are
         actually rendered to canvas at any moment, so placing/dragging
         signatures still works anywhere without mounting all pages of a
         large (2000-4000+) document at once. -->
    <app-pdf-virtual-viewer
      #pdfViewer
      [pdfData]="convertedPDf"
      (docLoaded)="onDocLoaded($event)"
      (pageRendered)="onPageRendered($event)"
    >
      <ng-template let-pageNum let-width="width" let-height="height">
        <div
          class="dd-page-click-target"
          [style.width.px]="width"
          [style.height.px]="height"
          [class.is-carrying]="!!carryingSigner"
          (click)="onPageClick($event, pageNum, $any($event.currentTarget))"
        >
          <!-- Read-only reference markers for positions already fixed through
               Pre-Defined placement. pointer-events are disabled in CSS so they
               never intercept a click meant for placing a new box. -->
          <div
            *ngFor="let marker of getReferenceMarkersForPage(pageNum)"
            class="dd-reference-box"
            [style.left.px]="marker.xFraction * width"
            [style.top.px]="marker.yFraction * height"
            [style.width.px]="marker.widthFraction * width"
            [style.height.px]="marker.heightFraction * height"
            [style.border-color]="getReferenceColor(marker)"
            [style.background-image]="getReferenceStripes(marker)"
            [attr.title]="
              marker.name + ' — pre-defined ' + marker.positionLabel + ' (not editable here)'
            "
          >
            <div class="dd-reference-header">
              <span class="dd-reference-name" [style.color]="getReferenceColor(marker)">
                {{ marker.name }}
              </span>
              <i
                class="pi pi-lock dd-reference-lock"
                [style.color]="getReferenceColor(marker)"
              ></i>
            </div>
            <span class="dd-reference-pos" [style.color]="getReferenceColor(marker)">
              {{ marker.positionLabel }}
            </span>
          </div>

          <!-- Placed sign boxes for this page -->
          <ng-container *ngFor="let item of getSignerListForPage(pageNum)">
            <div
              *ngFor="let field of item?.signBoxList"
              class="sign-box"
              [class.sign-box-readonly]="isSignerReadOnly(item.id)"
              [style.background-color]="getSignerColor(item.id) + '22'"
              [style.border-color]="getSignerColor(item.id)"
              [style.opacity]="isSignerReadOnly(item.id) ? '0.6' : '1'"
              [style.top.px]="getPosition(field, pageNum).y"
              [style.left.px]="getPosition(field, pageNum).x"
              [style.width.px]="SIGN_BOX_WIDTH_PX"
              [style.height.px]="SIGN_BOX_HEIGHT_PX"
              cdkDrag
              [cdkDragDisabled]="isSignerReadOnly(item.id)"
              cdkDragBoundary=".page-boundary"
              (cdkDragMoved)="onDragMoved($event)"
              (cdkDragEnded)="dragEnded($event, item, field, pageNum)"
              (click)="$event.stopPropagation()"
            >
              <div class="sign-box-header">
                <p class="sign-box-name" [style.color]="getSignerColor(item.id)">
                  {{ getSignerNameById(item.id) }}
                </p>
                <span
                  *ngIf="!isSignerReadOnly(item.id)"
                  class="sign-box-trash"
                  (click)="removeSignBox(item, field, pageNum); $event.stopPropagation()"
                >
                  <i class="pi pi-trash"></i>
                </span>
              </div>

              <div class="sign-box-arrow">
                <i class="pi pi-arrow-down"></i>
              </div>

              <div class="signature-options" *ngIf="!isSignerReadOnly(item.id)">
                <app-dropdown
                  [options]="signaturePlacementOptions"
                  [(ngModel)]="field.placementOption"
                  placeholder="Placement"
                  (onChange)="onSignaturePlacementChange($event, item, field, pageNum)"
                  styleClass="p-inputtext-sm compact-dropdown"
                  [style]="{ width: '100%', 'font-size': '0.7rem', 'min-height': '24px' }"
                >
                  <ng-template pTemplate="selectedItem" let-selectedItem>
                    <div style="font-size: 0.7rem; line-height: 1.2">
                      {{ selectedItem?.label }}
                    </div>
                  </ng-template>
                  <ng-template pTemplate="item" let-option>
                    <div style="font-size: 0.7rem; padding: 4px 8px; line-height: 1.2">
                      {{ option.label }}
                    </div>
                  </ng-template>
                </app-dropdown>
              </div>
            </div>
          </ng-container>
        </div>
      </ng-template>
    </app-pdf-virtual-viewer>
  </div>

  <!-- ── Ghost box — follows cursor while carrying ───────────────────── -->
  <div
    *ngIf="carryingSigner"
    class="dd-ghost-box"
    [style.left.px]="ghostLeft"
    [style.top.px]="ghostTop"
    [style.background-color]="getCarryingSignerColor() + '22'"
    [style.border-color]="getCarryingSignerColor()"
  >
    <i class="pi pi-pen-to-square"></i>
    <span>{{ carryingSigner?.name || carryingSigner }}</span>
    <span class="dd-ghost-hint">click to place</span>
  </div>

</div>